import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useMatch } from "react-router-dom";
import { useStores } from "@stores";
import { ROUTES } from "@stores/AccountStore";
import Tabs from "@components/Tabs";
import Text from "@components/Text";
import AdminUsersTab from "./AdminUsersTab";
import AdminDevicesTab from "./AdminDevicesTab";
import AdminMessagesTab from "./AdminMessagesTab";
import AdminDeviceLogsTab from "./AdminDeviceLogsTab";
import AdminSettingsSchemaTab from "./AdminSettingsSchemaTab";
import DefaultAnalyticsSettingsTab from "./DefaultAnalyticsSettingsTab";
import CommonAnalyticsParamsTab from "./CommonAnalyticsParamsTab";
import AdminApiDocsTab from "./AdminApiDocsTab";
import AdminApiKeysTab from "./AdminApiKeysTab";
import AdminNotificationsTab from "./AdminNotificationsTab";
import AdminConfigsTab from "./AdminConfigsTab";
import AdminUserDetailPage from "./AdminUserDetailPage";
import AdminDeviceDetailPage from "./AdminDeviceDetailPage";
import { checkAdmin } from "@services/adminService";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";

const Root = styled.div`
  min-height: 100vh;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 32px;
  box-sizing: border-box;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 0 16px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-bottom: 1px solid #ebeeef;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 12px 0;
    margin-bottom: 16px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const BackLink = styled.a`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #4a90d9;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
`;

const BASE_TABS = [
  { name: "Users" },
  { name: "Devices" },
  { name: "Messages" },
  { name: "Device Logs" },
  { name: "User settings schema" },
  { name: "Default analytics" },
  { name: "Common parameters" },
  { name: "API Docs" },
  { name: "Notifications" },
  { name: "Configs" },
];
const TABS_WITH_API_KEYS = [...BASE_TABS, { name: "API Keys" }];

const AdminScreen: React.FC = () => {
  const { accountStore } = useStores();
  const [activeTab, setActiveTab] = useState(0);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const userDetailMatch = useMatch(`${ROUTES.ADMIN}/users/:id`);
  const deviceDetailMatch = useMatch(`${ROUTES.ADMIN}/devices/:esn`);

  const tabs = isSuperAdmin ? TABS_WITH_API_KEYS : BASE_TABS;
  const apiKeysTabIndex = BASE_TABS.length;

  useEffect(() => {
    checkAdmin()
      .then((result) => setIsSuperAdmin(result.isSuperAdmin === true))
      .catch(() => {});
  }, []);

  // Reset activeTab if it becomes out of bounds (e.g. Super Admin status changes)
  useEffect(() => {
    if (activeTab >= tabs.length) {
      setActiveTab(0);
    }
  }, [activeTab, tabs.length]);

  // Add noindex meta tag
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  return (
    <Root>
      <Header>
        <HeaderLeft>
          <Text size={14} color="#899093">
            Admin Console
          </Text>
        </HeaderLeft>
        <HeaderRight>
          <Text size={13} color="#899093">
            {accountStore.account?.email}
          </Text>
          <BackLink href="/fields">Back to app</BackLink>
        </HeaderRight>
      </Header>

      {userDetailMatch ? (
        <AdminUserDetailPage userId={userDetailMatch.params.id!} />
      ) : deviceDetailMatch ? (
        <AdminDeviceDetailPage esn={deviceDetailMatch.params.esn!} />
      ) : (
        <>
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            setActive={setActiveTab}
            style={{ marginBottom: 24 }}
          />

          {activeTab === 0 && <AdminUsersTab />}
          {activeTab === 1 && <AdminDevicesTab />}
          {activeTab === 2 && <AdminMessagesTab />}
          {activeTab === 3 && <AdminDeviceLogsTab />}
          {activeTab === 4 && <AdminSettingsSchemaTab />}
          {activeTab === 5 && <DefaultAnalyticsSettingsTab />}
          {activeTab === 6 && <CommonAnalyticsParamsTab />}
          {activeTab === 7 && <AdminApiDocsTab />}
          {activeTab === 8 && <AdminNotificationsTab />}
          {activeTab === 9 && <AdminConfigsTab />}
          {activeTab === apiKeysTabIndex && <AdminApiKeysTab />}
        </>
      )}
    </Root>
  );
};

export default observer(AdminScreen);
